import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@mealplan/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can
 * match **ZERO rows and return "success"**. This is not a hypothesis: it was
 * measured in `space-academy-app` (same canon, local stack's `activity` table,
 * course `06a131d0`) — the course is created, 1.03 s later the unit is created, and
 * **between them there is NO `course/updated` op**: the title the author had just
 * written never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the
 * local view from the latest checkpoint**. If the checkpoint does not yet
 * contain the newly created row, the row is **ABSENT for that instant** — an
 * UPDATE against it writes nothing, generates no op, and **does not fail**. In a
 * meal planner the normal case is exactly the creation flow:
 * create a recipe and immediately start editing it (name, nutrition), or
 * generate the weekly shopping list right after planning.
 *
 * Why the absence of an error proves nothing here: the server's write path
 * returns **2xx deliberately** (a 4xx would block the client's queue forever).
 * That case is a DIFFERENT path—there the server rejects and stays silent—but
 * the criterion is the same: a user write that disappears without a trace is
 * the worst failure mode.
 *
 * Contract of this module: write, **READ BACK**, and compare. If the value did
 * not land, retry with backoff; if it still has not landed when the retries
 * are exhausted, **THROW**—the caller reports it instead of leaving the user
 * looking at a field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberately) ──────────────────────────
 * Hard `DELETE`s are not verified: their natural check would be "the row is
 * no longer there", but "absent" is precisely the ambiguous state in this
 * window—we cannot distinguish "it was deleted" from "it had not arrived yet".
 *
 * ── Second class: the `upsert` that decides INSERT-vs-UPDATE with its own SELECT ─
 *
 * `patchRowVerified`/`waitForRowPresent` protect the write against a row
 * whose `id` is ALREADY known. There is a different class: an upsert that decides whether to
 * INSERT or reuse with its own `SELECT`, because there is no `id` to read back
 * until that branch is decided (`ensureAislesByNameMutation` — aisle by
 * NAME — and the `shopping_list` upsert by WEEK in `generate.ts`). That
 * `SELECT` falls into the SAME checkpoint window: if the row exists but
 * has just reappeared after a rebuild, the `SELECT` sees it as absent and the code
 * INSERTs a new row—the data is not lost, the row is DUPLICATED, with no
 * visible error.
 *
 * `readMatchingRowVerified` (below) closes this: both cases in this
 * repo are identified by CONTENT (aisle name, week), not by an
 * `id` the caller already knows, so the explicit `isNew` shortcut does not
 * apply (see `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §3 for the complete
 * criterion and why they are not interchangeable). Primitive brought
 * verbatim from the canon (`listto-app@8d4240fd`, originating in
 * `stockpilot-app@b8c4bc8a`) — do not reinvent a local variant.
 */

/**
 * Backoff for retries. Sums to ≈ 6.05 s: amply covers the round trip of
 * a checkpoint (measured at ~1 s against the local stack) without leaving a
 * quick interaction—marking bought, archiving a recipe—hanging forever if the row
 * truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Wait times between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error for a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  const rows = await db.select().from(table).where(eq(idColumn, id)).limit(1);
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when compared against SQLite.*/
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * `UPDATE … WHERE id = ?` **verified**: writes, reads back, compares the patch
 * fields, and retries while they have not landed. Throws `LocalWriteLostError`
 * if they are still missing when the backoff is exhausted.
 *
 * `updated_at` is set by this function on every attempt (and is not compared: it changes
 * each pass); `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it.
 *
 * For mutations that are not a simple patch—a transaction that
 * updates a recipe and REPLACES its ingredient set all at once
 * (`updateRecipeMutation`)—retrying inside the transaction is not
 * appropriate (it would sleep with the write lock held), and operating on an
 * absent row would be worse than an `UPDATE` that matches nothing: the
 * transaction's sibling ingredient `DELETE`/`INSERT` would still happen,
 * leaving the recipe with new ingredients but its name/portions untouched.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<missing row>"]);
    }
    await sleep(wait);
  }
}

/**
 * Re-reads, with the same backoff as the rest of the module, a row that is
 * identified by CONTENT instead of by `id`—the case of
 * `ensureAislesByNameMutation` (aisle by name) and the
 * `shopping_list` upsert by week in `generate.ts` (see the "Second class"
 * note above).
 *
 * It is called ALWAYS before opening the transaction that decides INSERT-vs-UPDATE, and
 * NEVER inside it—for the same reason as `waitForRowPresent`: retrying with
 * the write lock already held makes the whole transaction sleep, not just the
 * read. When there are several rows to resolve (several aisle names for
 * a recipe, for example), resolve them in PARALLEL (`Promise.all`) before the
 * transaction: a genuinely new row then does not pay the latency of
 * another row's backoff that did require retries.
 *
 * Unlike `patchRowVerified`/`waitForRowPresent`, exhausting the backoff
 * is NOT an error here: genuine absence (the first time that content combination
 * is touched) is a VALID result, so this function returns `undefined` instead
 * of throwing, and the caller proceeds to INSERT exactly as if it had not
 * retried. The backoff only reduces the false-negative window; it cannot
 * eliminate it entirely without another signal (the CRUD queue itself).
 *
 * `read` must repeat the SAME query on every attempt—a function that
 * rebuilds the `where` on `db` each time, not an already-resolved promise that
 * would be reused without touching the database again.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
